import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Music, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import { INTRO_COPY, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Spinner,
  Textarea,
} from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { useMediaUrl } from '@/lib/media-url';
import { formatPhone } from '@/lib/format';
import { uploadMedia } from '@/lib/upload';
import { useRole } from '@/store/auth';

interface Intro {
  name: string;
  logoKey: string | null;
  address: string | null;
  phone: string | null;
  motto: string | null;
  greeting: string | null;
  symbols: { label: string; value: string }[];
  songTitle: string | null;
  songMediaId: string | null;
  frameMediaId: string | null;
}

/**
 * 원 소개 — §5.26. 원훈·인사말·상징(원화·원목 등)·원가(노래).
 * 원장이 채우고, 학부모·교직원 모두가 본다. 원가는 mp3를 올리면 앱에서 바로 재생된다.
 */
export function AboutPage() {
  const isDirector = useRole() === ROLES.DIRECTOR;
  const [editing, setEditing] = useState(false);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['facility', 'intro'],
    queryFn: () => apiData<Intro>('/facilities/current/intro'),
  });

  if (isPending)
    return (
      <Screen>
        <AppBar title={INTRO_COPY.title} back />
        <Spinner />
      </Screen>
    );
  if (isError || !data)
    return (
      <Screen>
        <AppBar title={INTRO_COPY.title} back />
        <ErrorNote message="불러오지 못했어요." onRetry={() => void refetch()} />
      </Screen>
    );

  const hasContent =
    data.motto || data.greeting || data.symbols.length > 0 || data.songMediaId;

  return (
    <Screen>
      <AppBar title={INTRO_COPY.title} back />

      {editing ? (
        <IntroEditor intro={data} onClose={() => setEditing(false)} />
      ) : (
        <div className="flex flex-col gap-3 p-4">
          {isDirector && (
            <Button variant="outline" icon={<Pencil size={16} />} onClick={() => setEditing(true)}>
              소개 수정
            </Button>
          )}

          <Card>
            <p className="text-lg font-bold">{data.name}</p>
            {data.address && <p className="mt-1 text-sm text-ink-sub">{data.address}</p>}
            {data.phone && <p className="text-sm text-ink-sub">{formatPhone(data.phone)}</p>}
          </Card>

          {!hasContent && (
            <EmptyState title={INTRO_COPY.emptyTitle} body={INTRO_COPY.emptyBody} />
          )}

          {data.motto && (
            <Card className="bg-primary-soft">
              <p className="text-sm font-semibold text-primary-strong">{INTRO_COPY.mottoLabel}</p>
              <p className="mt-1 text-lg font-bold leading-snug">{data.motto}</p>
            </Card>
          )}

          {data.greeting && (
            <Card>
              <p className="mb-1 text-sm font-semibold text-ink-sub">{INTRO_COPY.greetingLabel}</p>
              <p className="whitespace-pre-wrap text-base leading-relaxed">{data.greeting}</p>
            </Card>
          )}

          {data.symbols.length > 0 && (
            <Card>
              <p className="mb-2 text-sm font-semibold text-ink-sub">{INTRO_COPY.symbolsLabel}</p>
              <dl className="flex flex-col gap-1.5">
                {data.symbols.map((symbol) => (
                  <div key={symbol.label} className="flex items-baseline gap-3">
                    <dt className="w-16 shrink-0 text-sm font-semibold">{symbol.label}</dt>
                    <dd className="text-base">{symbol.value}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          )}

          {data.songMediaId && (
            <Card>
              <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink-sub">
                <Music size={14} /> {INTRO_COPY.songLabel}
                {data.songTitle && <span className="font-normal">· {data.songTitle}</span>}
              </p>
              <SongPlayer mediaId={data.songMediaId} />
            </Card>
          )}
        </div>
      )}
    </Screen>
  );
}

/** 원가 재생 — 열람 URL을 받아 브라우저 오디오로 튼다 */
function SongPlayer({ mediaId }: { mediaId: string }) {
  const { data } = useMediaUrl(mediaId);
  if (!data) return <Spinner />;
  return <audio controls preload="metadata" className="w-full" src={data.url} />;
}

function IntroEditor({ intro, onClose }: { intro: Intro; onClose: () => void }) {
  const queryClient = useQueryClient();
  const songRef = useRef<HTMLInputElement>(null);
  const frameRef = useRef<HTMLInputElement>(null);

  const [motto, setMotto] = useState(intro.motto ?? '');
  const [greeting, setGreeting] = useState(intro.greeting ?? '');
  const [symbols, setSymbols] = useState(
    intro.symbols.length > 0 ? intro.symbols : [{ label: '원화', value: '' }],
  );
  const [songTitle, setSongTitle] = useState(intro.songTitle ?? '');
  const [songMediaId, setSongMediaId] = useState(intro.songMediaId ?? '');
  const [frameMediaId, setFrameMediaId] = useState(intro.frameMediaId ?? '');
  const [busy, setBusy] = useState<'song' | 'frame' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiData('/facilities/current/intro', {
        method: 'PATCH',
        body: {
          motto,
          greeting,
          symbols: symbols.filter((s) => s.label.trim() && s.value.trim()),
          songTitle,
          songMediaId,
          frameMediaId,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facility', 'intro'] });
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : '저장하지 못했어요.'),
  });

  const handleUpload = async (kind: 'song' | 'frame', file: File) => {
    setBusy(kind);
    setError(null);
    try {
      const mediaId = await uploadMedia(file);
      if (kind === 'song') setSongMediaId(mediaId);
      else setFrameMediaId(mediaId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '올리지 못했어요.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4">
      <Field label={INTRO_COPY.mottoLabel}>
        <Input value={motto} onChange={(e) => setMotto(e.target.value)} placeholder="예: 스스로 자라고 더불어 크는 아이" />
      </Field>

      <Field label={INTRO_COPY.greetingLabel}>
        <Textarea
          rows={4}
          value={greeting}
          onChange={(e) => setGreeting(e.target.value)}
          placeholder="학부모님께 전하는 원장 인사말"
        />
      </Field>

      <Field label={`${INTRO_COPY.symbolsLabel} (원화·원목·원조 등)`}>
        <div className="flex flex-col gap-1.5">
          {symbols.map((symbol, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-24 shrink-0">
                <Input
                  value={symbol.label}
                  placeholder="원화"
                  onChange={(e) =>
                    setSymbols((prev) =>
                      prev.map((s, i) => (i === index ? { ...s, label: e.target.value } : s)),
                    )
                  }
                />
              </div>
              <Input
                value={symbol.value}
                placeholder="해바라기"
                onChange={(e) =>
                  setSymbols((prev) =>
                    prev.map((s, i) => (i === index ? { ...s, value: e.target.value } : s)),
                  )
                }
              />
              <button
                type="button"
                aria-label="항목 삭제"
                className="tap grid h-9 w-9 shrink-0 place-items-center rounded-sm text-ink-sub hover:text-notice"
                onClick={() => setSymbols((prev) => prev.filter((_, i) => i !== index))}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <Button
            size="sm"
            variant="outline"
            icon={<Plus size={14} />}
            onClick={() => setSymbols((prev) => [...prev, { label: '', value: '' }])}
          >
            항목 추가
          </Button>
        </div>
      </Field>

      <Field label={`${INTRO_COPY.songLabel} (mp3)`}>
        <div className="flex items-center gap-2">
          <Input
            value={songTitle}
            onChange={(e) => setSongTitle(e.target.value)}
            placeholder="노래 제목"
          />
          <Button
            size="sm"
            variant="outline"
            icon={<Upload size={14} />}
            loading={busy === 'song'}
            onClick={() => songRef.current?.click()}
          >
            {songMediaId ? '교체' : '올리기'}
          </Button>
        </div>
        {songMediaId && (
          <div className="mt-2">
            <SongPlayer mediaId={songMediaId} />
          </div>
        )}
        <input
          ref={songRef}
          type="file"
          accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/wav"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload('song', file);
            e.target.value = '';
          }}
        />
      </Field>

      <Field label="인스타 프레임 (앨범 편집기에서 사용, PNG 권장)">
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 truncate text-sm text-ink-sub">
            {frameMediaId ? '프레임이 등록되어 있어요.' : '아직 프레임이 없어요.'}
          </p>
          <Button
            size="sm"
            variant="outline"
            icon={<Upload size={14} />}
            loading={busy === 'frame'}
            onClick={() => frameRef.current?.click()}
          >
            {frameMediaId ? '교체' : '올리기'}
          </Button>
        </div>
        <input
          ref={frameRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload('frame', file);
            e.target.value = '';
          }}
        />
      </Field>

      {error && (
        <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button block loading={save.isPending} onClick={() => save.mutate()}>
          저장
        </Button>
        <Button block variant="outline" onClick={onClose}>
          닫기
        </Button>
      </div>
    </div>
  );
}
